import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useMagnetic from '../hooks/useMagnetic';
import { scrollToSection } from '../hooks/useSmoothScroll';
import { Facebook, Linkedin, Instagram, Mail, Phone, MapPin } from './Icons';
import './Footer.css';

gsap.registerPlugin(ScrollTrigger);

const QUICK = [
  { label: 'Home', href: '#home' },
  { label: 'Who We Are', href: '#who' },
  { label: 'Solutions', href: '#solutions' },
  { label: 'Enquiry', href: '#enquiry' },
  { label: 'Contact', href: '#contact' },
];

const SOLUTIONS = [
  'Solar Kit and Installation',
  'Engineering & Design',
  'Operations & Maintenance',
  'Energy Storage',
];

const SOCIAL = [
  { Icon: Facebook, label: 'Facebook', href: '#' },
  { Icon: Linkedin, label: 'LinkedIn', href: '#' },
  { Icon: Instagram, label: 'Instagram', href: '#' },
  { Icon: Mail, label: 'Email', href: 'mailto:info@csolar.cc' },
];

export default function Footer() {
  const rootRef = useRef(null);
  const ctaRef = useRef(null);
  const btnRef = useMagnetic(0.4);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(ctaRef.current.children, {
        opacity: 0, y: 22, duration: 0.8, stagger: 0.12, ease: 'power3.out',
        scrollTrigger: { trigger: rootRef.current, start: 'top 78%' },
      });
    }, rootRef);
    return () => ctx.revert();
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    scrollToSection(href);
  };

  return (
    <footer className="footer" ref={rootRef}>
      {/* CTA banner */}
      <div className="footer-cta" ref={ctaRef}>
        <div>
          <h2 className="footer-cta-title">Ready to power <span className="accent">your world?</span></h2>
          <p className="footer-cta-sub">Let's build a sustainable future together.</p>
        </div>
        <a href="#enquiry" className="get-in-touch" ref={btnRef} onClick={e => go(e, '#enquiry')}>
          <span>Get in touch</span>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 6h10M6 1l5 5-5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      </div>

      {/* Columns */}
      <div className="footer-grid">
        <div className="footer-brand">
          <a href="#home" className="footer-logo" onClick={e => go(e, '#home')}>
            <span className="footer-logo-mark" />C-Solar
          </a>
          <p className="footer-tagline">
            Delivering reliable, efficient, and sustainable solar energy
            solutions for a brighter tomorrow.
          </p>
          <div className="footer-social">
            {SOCIAL.map(({ Icon, label, href }) => (
              <a key={label} href={href} className="social-btn" aria-label={label}>
                <Icon width="16" height="16" />
              </a>
            ))}
          </div>
        </div>

        <nav className="footer-col">
          <p className="footer-col-title">Quick Links</p>
          {QUICK.map(l => (
            <a key={l.href} href={l.href} onClick={e => go(e, l.href)}>{l.label}</a>
          ))}
        </nav>

        <nav className="footer-col">
          <p className="footer-col-title">Solutions</p>
          {SOLUTIONS.map(s => (
            <a key={s} href="#solutions" onClick={e => go(e, '#solutions')}>{s}</a>
          ))}
        </nav>

        <div className="footer-col footer-contact">
          <p className="footer-col-title">Contact Us</p>
          <a href="tel:+914428300000" className="contact-line">
            <span className="contact-ico"><Phone width="15" height="15" /></span>
            +91 44 2830 0000
          </a>
          <a href="mailto:info@csolar.cc" className="contact-line">
            <span className="contact-ico"><Mail width="15" height="15" /></span>
            info@csolar.cc
          </a>
          <span className="contact-line">
            <span className="contact-ico"><MapPin width="15" height="15" /></span>
            Shop 138, Chandresen Building,<br />Balaji Nagar, Ekkatutangal,<br />Chennai, India
          </span>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="footer-bar">
        <p>© 2026 C-Solar. All rights reserved.</p>
        <div className="footer-legal">
          <a href="#">Privacy Policy</a>
          <span className="footer-dot">·</span>
          <a href="#">Terms &amp; Conditions</a>
        </div>
      </div>
    </footer>
  );
}
